// RepoPickerPill.jsx — composer footer pill to pick a GitHub repo.
//
// Shown only when the Code scope is "github". Matches the PillDropdown shape
// (h-7 rounded-full) and opens a shad-pro cmdk command panel listing every
// repo from GET /api/github/repos. Picking a repo stores it in the composer
// UI prefs (ui.github_repo) so "Criar sessão" opens/clones that repo.

import { useEffect, useRef, useState } from 'react';
import { Command as CmdkCommand } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Github, Loader2, Lock, Plus } from 'lucide-react';
import { apiFetch } from '../../lib/api.js';
import { useAgentStore } from '../../store/useAgentStore.js';
import { getRecentRepos, pushRecentRepo } from '../../lib/recentRepos.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

export default function RepoPickerPill() {
  const selected = useAgentStore((s) => s.code.ui.github_repo) || null;
  const setPref = useAgentStore((s) => s.setUiPref);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [repos, setRepos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [recents, setRecents] = useState(() => getRecentRepos());
  // Estado do fluxo "+ Novo repositório".
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  const wrapRef = useRef(null);

  // Lazy-load repos the first time the panel opens.
  useEffect(() => {
    if (!open || loaded) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setNotConnected(false);
      const res = await apiFetch('/api/github/repos', { method: 'GET' }).catch(() => ({ ok: false }));
      if (cancelled) return;
      if (!res.ok) {
        if (res.status === 404 || res.code === 'GITHUB_NOT_CONNECTED') setNotConnected(true);
        else setError(res.error || 'Falha ao carregar repositórios');
      } else {
        setRepos(Array.isArray(res.data?.repos) ? res.data.repos : []);
        setLoaded(true);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, loaded]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Sai do modo "criar" sempre que o painel fecha; recarrega recentes ao abrir.
  useEffect(() => {
    if (open) {
      setRecents(getRecentRepos());
    } else {
      setCreating(false);
      setNewName('');
      setCreateError(null);
    }
  }, [open]);

  function pick(repo) {
    const entry = {
      name: repo.name,
      full_name: repo.full_name,
      url: repo.url,
      clone_url: repo.clone_url,
      default_branch: repo.default_branch || 'main',
      private: !!repo.private,
    };
    setPref('github_repo', entry);
    // Branch pill follows the repo's default branch for a coherent session.
    if (repo.default_branch) setPref('branch', repo.default_branch);
    setRecents(pushRecentRepo(entry));
    setOpen(false);
  }

  function clearSelection(e) {
    e.stopPropagation();
    setPref('github_repo', null);
  }

  // Cria um repo novo na conta do GitHub e já o seleciona no composer.
  async function handleCreate() {
    const name = newName.trim();
    if (!name || createBusy) return;
    setCreateBusy(true);
    setCreateError(null);
    const res = await apiFetch('/api/github/repos', {
      method: 'POST',
      body: JSON.stringify({ name, private: true }),
    }).catch(() => ({ ok: false }));
    setCreateBusy(false);
    if (!res.ok) {
      if (res.status === 404 || res.code === 'GITHUB_NOT_CONNECTED') setNotConnected(true);
      else setCreateError(res.error || 'Falha ao criar repositório');
      return;
    }
    const repo = res.data?.repo;
    if (repo) {
      setRepos((prev) => [repo, ...prev]); // aparece no topo da lista
      pick(repo); // seleciona + fecha
    }
    setCreating(false);
    setNewName('');
  }

  const label = selected ? (selected.name || selected.full_name) : 'Repositório';

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `Repositório selecionado: ${selected.full_name}. Trocar.` : 'Escolher repositório do GitHub'}
        className={
          'group inline-flex h-7 max-w-[200px] items-center gap-1.5 rounded-full border px-2.5 text-[11px] leading-none transition-colors ' +
          (selected
            ? 'border-apolo-highlightRing bg-apolo-highlightMuted/40 text-apolo-textPrimary'
            : 'border-apolo-borderSubtle bg-apolo-surface1 text-apolo-textSecondary hover:border-apolo-borderStrong')
        }
      >
        <Github size={12} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
        <span className="truncate font-medium">{label}</span>
        {selected ? (
          <span
            role="button"
            tabIndex={0}
            onClick={clearSelection}
            onKeyDown={(e) => { if (e.key === 'Enter') clearSelection(e); }}
            aria-label="Limpar repositório selecionado"
            className="ml-0.5 shrink-0 rounded px-1 text-apolo-textTertiary hover:text-apolo-textPrimary"
          >
            ×
          </span>
        ) : (
          <ChevronDown size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary opacity-70" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={SPRING}
            className="absolute bottom-full left-0 z-40 mb-1 w-72 overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-[var(--apolo-glass-card-shadow)]"
          >
            {creating ? (
              // Modo criar: input de nome + ação. Enter cria, Esc volta.
              <div className="flex flex-col gap-2 p-3">
                <div className="flex items-center gap-2 text-[11px] font-medium text-apolo-textSecondary">
                  <Plus size={13} aria-hidden="true" className="text-apolo-textTertiary" />
                  Novo repositório
                </div>
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
                    if (e.key === 'Escape') { e.preventDefault(); setCreating(false); }
                  }}
                  placeholder="nome-do-repositorio"
                  aria-label="Nome do novo repositório"
                  className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
                />
                <p className="text-[10px] text-apolo-textTertiary">Privado, inicializado com README na sua conta GitHub.</p>
                {createError && <p className="text-[11px] text-apolo-error" role="alert">{createError}</p>}
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setCreating(false); setCreateError(null); }}
                    className="inline-flex h-7 items-center rounded-md px-2 text-[11px] text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!newName.trim() || createBusy}
                    className="inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] font-medium text-apolo-textPrimary hover:border-apolo-accent disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {createBusy ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : <Plus size={11} aria-hidden="true" />}
                    Criar
                  </button>
                </div>
              </div>
            ) : (
            <CmdkCommand label="Repositórios do GitHub" className="flex flex-col">
              <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-2">
                <Github size={13} aria-hidden="true" className="text-apolo-textTertiary" />
                <CmdkCommand.Input
                  autoFocus
                  placeholder="Buscar repositório…"
                  className="w-full bg-transparent text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                />
              </div>
              {!loading && !notConnected && !error && (
                <button
                  type="button"
                  onClick={() => { setCreating(true); setCreateError(null); }}
                  className="flex w-full items-center gap-1.5 border-b border-apolo-borderSubtle px-3 py-2 text-left text-[12px] font-medium text-apolo-accent hover:bg-apolo-surface2"
                >
                  <Plus size={13} aria-hidden="true" />
                  Novo repositório
                </button>
              )}
              <CmdkCommand.List className="max-h-64 overflow-y-auto p-1">
                {loading && (
                  <div className="flex items-center justify-center gap-2 py-6 text-[11px] text-apolo-textTertiary">
                    <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    Buscando repositórios…
                  </div>
                )}
                {!loading && notConnected && (
                  <div className="flex flex-col items-start gap-2 px-2 py-3 text-[11px] text-apolo-textSecondary">
                    Conecte o GitHub para listar seus repositórios.
                    <button
                      type="button"
                      onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('apolo:openConnections')); }}
                      className="inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] font-medium text-apolo-textPrimary hover:border-apolo-highlightRing"
                    >
                      <Github size={11} aria-hidden="true" />
                      Conectar GitHub
                    </button>
                  </div>
                )}
                {!loading && error && (
                  <div className="px-2 py-3 text-[11px] text-apolo-error" role="alert">{error}</div>
                )}
                {!loading && !notConnected && !error && (() => {
                  const recentSet = new Set(recents.map((r) => r.full_name));
                  const rest = repos.filter((r) => !recentSet.has(r.full_name));
                  const itemCls =
                    'flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[12px] text-apolo-textSecondary data-[selected=true]:bg-apolo-surface2 data-[selected=true]:text-apolo-textPrimary';
                  const renderItem = (repo) => (
                    <CmdkCommand.Item
                      key={repo.id || repo.full_name}
                      value={repo.full_name || repo.name}
                      keywords={[repo.name].filter(Boolean)}
                      onSelect={() => pick(repo)}
                      className={itemCls}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        {repo.private && <Lock size={10} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />}
                        <span className="truncate">{repo.full_name || repo.name}</span>
                      </span>
                      {repo.language && (
                        <span className="shrink-0 text-[10px] text-apolo-textTertiary">{repo.language}</span>
                      )}
                    </CmdkCommand.Item>
                  );
                  const headingCls =
                    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-apolo-textTertiary';
                  return (
                    <>
                      <CmdkCommand.Empty className="px-2 py-3 text-[11px] text-apolo-textTertiary">
                        Nenhum repositório encontrado.
                      </CmdkCommand.Empty>
                      {recents.length > 0 && (
                        <CmdkCommand.Group heading="Recentes" className={headingCls}>
                          {recents.map(renderItem)}
                        </CmdkCommand.Group>
                      )}
                      <CmdkCommand.Group heading={recents.length > 0 ? 'Todos' : undefined} className={headingCls}>
                        {rest.map(renderItem)}
                      </CmdkCommand.Group>
                    </>
                  );
                })()}
              </CmdkCommand.List>
            </CmdkCommand>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
